import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { fetchInitContext, fetchSecureForm } from './features/thunks'
import type { RootState } from './'
import type { ContextInitState } from './features/types'
import { TDashboardWidgets } from '@view/components/DashboardWidgets/types'

const initialState: ContextInitState = {
	currentBusinessAddressAsString: '',
	bowaterRegNumber: '',
	init: false,
	userContext: '',
	context: '',
	dashType: null,
	currentBusinessId: '',
	currentBusiness: '',
	personId: '',
	personFullName: '',
	operatorName: '',
	operatorId: '',
	userName: '',
	siteTheme: '',
	supplierLogo: '',
	userRoleName: '',
	currencyCode: '',
	countryCode: '',
	currency: 'GBP',
	driverId: '',
	loading: false,
	cancelNetworkRequests: false,
	formUid: {},
	permissions: {},
	memberAreaBusinessSwapperOptions: [],
	userDefaultSearchCategory: '',
	driver: null,
}
export const contextSlice = createSlice({
	name: 'context',
	initialState,
	reducers: {
		setTaxiData(state, action) {
			const { offerFodRide, offerUber } = action.payload

			state.offerFodRide = offerFodRide
			state.offerUber = offerUber
		},
		addToPermissions(state, action: PayloadAction<TDashboardWidgets>) {
			const { lockBooker, lockDriver, lockNewDriver } = action.payload
			state.permissions = { ...state.permissions, lockBooker, lockDriver, lockNewDriver }
		},
		setCancelNetworkRequests(state) {
			state.cancelNetworkRequests = true
		},
	},
	extraReducers: builder => {
		builder.addCase(fetchSecureForm.fulfilled, (state, action) => {
			state.formUid = action.payload
		})
		builder.addCase(fetchInitContext.pending, state => {
			state.loading = true
		})
		builder.addCase(fetchInitContext.rejected, state => {
			state.loading = false
		})
		builder.addCase(fetchInitContext.fulfilled, (state, action) => {
			const { context } = action.payload
			context.loading = false
			if (context) {
				const { dashType, permissions: userPermissions, siteTheme, context: siteContext } = context

				const newResPermissions = {
					cui: siteTheme == 'enterpriseMP' ? true : userPermissions?.['rental_manager.new_reservation'],
					oui: siteTheme == 'enterpriseMP' ? true : userPermissions?.['reservation_manager.new_reservation'],
					['cui-flex']: userPermissions?.['rental_manager.new_reservation'],
				}

				const fodPoolPermissions = {
					oui: userPermissions?.['reservation_manager.new_pool_reservation'],
					mui: userPermissions?.['rental_manager.new_pool_reservation'],
					dui: userPermissions?.['driver_manager.new_pool_reservation'],
					bui: userPermissions?.['rental_manager.new_pool_reservation'],
					cui: userPermissions?.['rental_manager.new_pool_reservation'],
				}

				const newResAccess = newResPermissions[dashType as keyof typeof newResPermissions]
				const fodPoolAccess = fodPoolPermissions[siteContext as keyof typeof fodPoolPermissions]
				const hasPoolFleetAccessOnly =
					fodPoolPermissions['cui'] &&
					dashType == 'cui' &&
					!newResAccess &&
					!userPermissions.offerFodRide &&
					!userPermissions.offerUber &&
					//from rental summary tile
					!userPermissions['rental_manager.live_reservations'] &&
					!userPermissions['rental_manager.tbas'] &&
					!userPermissions['rental_manager.all_reservations'] &&
					!userPermissions['rental_manager.book_taxi'] &&
					//from support services tile
					!userPermissions['rental_admin_manager.damage_manager'] &&
					!userPermissions['rental_admin_manager.parking_fines'] &&
					!userPermissions['rental_admin_manager.speeding_fines'] &&
					!userPermissions['rental_admin_manager.all_other_fines'] &&
					//from servicesWithPermissions
					!userPermissions['rental_admin_manager.customer_services'] &&
					!userPermissions['rental_admin_manager.drivers'] &&
					!userPermissions['rental_admin_manager.billing'] &&
					!userPermissions['rental_admin_manager.reports']

				const hasPoolFleetAndRentalAccess = dashType == 'cui' && fodPoolPermissions['cui'] && newResAccess
				const poolSummaryAccess = hasPoolFleetAccessOnly || hasPoolFleetAndRentalAccess

				const permissions = {
					...userPermissions,
					newResAccess,
					['rental_admin_manager.didYouKnow']: true,
					fodPoolAccess,
					hasPoolFleetAccessOnly,
					hasPoolFleetAndRentalAccess,
					poolSummaryAccess,
				}

				return { ...context, permissions, cancelNetworkRequests: false, formUid: state.formUid }
			}
		})
	},
})

export const selectContext = (state: RootState) => state.globalState

export const { addToPermissions, setCancelNetworkRequests, setTaxiData } = contextSlice.actions
